import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  tenderApi, 
  type CreateTenderDto, 
  type UpdateTenderDto,
  type UpdateStageDto, 
  type BidderDto, 
  type ScoreDto, 
  type DocUploadDto, 
  type AwardVendorDto,
  type TenderFilterDto
} from '../../../api/tenders';
import { notifications } from '@mantine/notifications';

// Query keys
export const tenderKeys = {
  all: ['tenders'] as const,
  lists: () => [...tenderKeys.all, 'list'] as const,
  list: (filter: TenderFilterDto) => [...tenderKeys.lists(), filter] as const,
  details: () => [...tenderKeys.all, 'detail'] as const,
  detail: (id: string) => [...tenderKeys.details(), id] as const,
  dashboard: () => [...tenderKeys.all, 'dashboard'] as const,
  expiring: () => [...tenderKeys.all, 'expiring'] as const,
};

// Queries
export const useTenders = (filter?: TenderFilterDto) => {
  return useQuery({
    queryKey: tenderKeys.list(filter || {}),
    queryFn: () => tenderApi.listTenders(filter),
  });
};

export const useTenderById = (id: string) => {
  return useQuery({
    queryKey: tenderKeys.detail(id),
    queryFn: () => tenderApi.getTenderById(id),
    enabled: !!id,
  });
};

export const useTenderDashboard = () => {
  return useQuery({
    queryKey: tenderKeys.dashboard(),
    queryFn: () => tenderApi.getTenderDashboard(),
  });
};

export const useExpiringTenders = () => {
  return useQuery({
    queryKey: tenderKeys.expiring(),
    queryFn: () => tenderApi.getExpiringTenders(),
  });
};

// Mutations
export const useCreateTender = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateTenderDto) => tenderApi.createTender(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenderKeys.dashboard() });
      notifications.show({
        title: 'Success',
        message: 'Tender created successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to create tender',
        color: 'red',
      });
    },
  });
};

export const useUpdateTender = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenderDto }) => 
      tenderApi.updateTender(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tenderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenderKeys.dashboard() });
      notifications.show({
        title: 'Success',
        message: 'Tender updated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update tender',
        color: 'red',
      });
    },
  });
};

export const useUpdateStage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStageDto }) => 
      tenderApi.updateStage(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tenderKeys.lists() });
      notifications.show({
        title: 'Success',
        message: 'Tender stage updated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update tender stage',
        color: 'red',
      });
    },
  });
};

export const useAddBidder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: BidderDto }) => 
      tenderApi.addBidder(tenderId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(tenderId) });
      notifications.show({
        title: 'Success',
        message: 'Bidder added successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to add bidder',
        color: 'red',
      });
    },
  });
};

export const useUploadDoc = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: DocUploadDto }) => 
      tenderApi.uploadDoc(tenderId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(tenderId) });
      notifications.show({
        title: 'Success',
        message: 'Document uploaded successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to upload document',
        color: 'red',
      });
    },
  });
};

export const useRecordTechScores = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tenderId, scores }: { tenderId: string; scores: ScoreDto[] }) => 
      tenderApi.recordTechScores(tenderId, scores),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(tenderId) });
      notifications.show({
        title: 'Success',
        message: 'Technical scores recorded successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to record technical scores',
        color: 'red',
      });
    },
  });
};

export const useRecordFinScores = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tenderId, scores }: { tenderId: string; scores: ScoreDto[] }) => 
      tenderApi.recordFinScores(tenderId, scores),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(tenderId) });
      notifications.show({
        title: 'Success',
        message: 'Financial scores recorded successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to record financial scores',
        color: 'red',
      });
    },
  });
};

export const useAwardVendor = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: AwardVendorDto }) => 
      tenderApi.awardVendor(tenderId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: tenderKeys.detail(tenderId) });
      queryClient.invalidateQueries({ queryKey: tenderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenderKeys.dashboard() });
      notifications.show({
        title: 'Success',
        message: 'Vendor awarded successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to award vendor',
        color: 'red',
      });
    },
  });
}; 